import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  getUserConversations, 
  getConversationMessages, 
  sendMessage, 
  markMessagesAsRead,
  subscribeToConversations,
  subscribeToMessages,
  formatMessageTime,
  getOtherParticipant
} from '../../utils/messaging';

const Messages = () => {
  const { currentUser } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [currentUserData, setCurrentUserData] = useState(null);

  useEffect(() => {
    if (currentUser) {
      fetchCurrentUserData();
      console.log('Setting up conversation subscription for user:', currentUser.uid);
      const unsubscribe = subscribeToConversations(currentUser.uid, (updatedConversations) => {
        console.log('Received conversations update:', updatedConversations);
        setConversations(updatedConversations);
        setLoading(false);
      });

      return () => unsubscribe();
    }
  }, [currentUser]);

  useEffect(() => {
    let unsubscribe = null;
    
    if (selectedConversation) {
      console.log('Setting up message subscription for conversation:', selectedConversation.id);
      unsubscribe = subscribeToMessages(selectedConversation.id, (updatedMessages) => {
        console.log('Received messages update:', updatedMessages);
        setMessages(updatedMessages);
        
        // Mark messages as read when viewing conversation
        if (updatedMessages.length > 0) {
          markMessagesAsRead(selectedConversation.id, currentUser.uid);
        }
      });
    }

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [selectedConversation, currentUser]);

  const fetchCurrentUserData = async () => {
    if (!currentUser) return;
    
    try {
      const { doc, getDoc } = await import('firebase/firestore');
      const { db } = await import('../../firebase');
      
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        setCurrentUserData({ id: userDoc.id, ...userDoc.data() });
      }
    } catch (error) {
      console.error('Error fetching current user data:', error);
    }
  };

  const handleConversationSelect = (conversation) => {
    setSelectedConversation(conversation);
    setMessages([]);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || !selectedConversation || !currentUser || !currentUserData) {
      return;
    }

    setSending(true);

    try {
      const otherParticipant = getOtherParticipant(selectedConversation, currentUser.uid);
      const otherParticipantId = selectedConversation.participants.find(id => id !== currentUser.uid);
      
      await sendMessage(
        selectedConversation.id,
        currentUser.uid,
        `${currentUserData.firstName} ${currentUserData.lastName}`.trim(),
        otherParticipantId,
        newMessage,
        'text'
      );

      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const renderConversationList = () => {
    if (conversations.length === 0) {
      return (
        <div className="no-conversations">
          <p>No conversations yet.</p>
          <p>Send a message to a provider to get started!</p>
        </div>
      );
    }

    return conversations.map(conversation => {
      const otherParticipant = getOtherParticipant(conversation, currentUser.uid);
      const unreadCount = conversation.unreadCount?.[currentUser.uid] || 0;
      const isSelected = selectedConversation?.id === conversation.id;

      return (
        <div 
          key={conversation.id} 
          className={`conversation-item ${isSelected ? 'selected' : ''}`}
          onClick={() => handleConversationSelect(conversation)}
        >
          <div className="conversation-avatar">
            {otherParticipant.profilePicture ? (
              <img 
                src={otherParticipant.profilePicture} 
                alt={otherParticipant.firstName} 
              />
            ) : (
              <div className="default-avatar">
                {otherParticipant.firstName?.[0]}{otherParticipant.lastName?.[0]}
              </div>
            )}
          </div>
          
          <div className="conversation-details">
            <div className="conversation-header">
              <h4>{otherParticipant.firstName} {otherParticipant.lastName}</h4>
              <span className="conversation-time">
                {formatMessageTime(conversation.lastActivity)}
              </span>
            </div>
            
            <div className="conversation-preview">
              <p className="last-message">
                {conversation.lastMessage?.content || 'No messages yet'}
              </p>
              {unreadCount > 0 && (
                <span className="unread-badge">{unreadCount}</span>
              )}
            </div>
          </div>
        </div>
      );
    });
  };

  const renderMessages = () => {
    if (!selectedConversation) {
      return (
        <div className="no-conversation-selected">
          <h3>Select a conversation to start messaging</h3>
          <p>Choose a conversation from the list to view and send messages.</p>
        </div>
      );
    }

    if (messages.length === 0) {
      return (
        <div className="no-messages">
          <p>No messages in this conversation yet.</p>
          <p>Send the first message to get started!</p>
        </div>
      );
    }

    return messages.map(message => {
      const isOwnMessage = message.senderId === currentUser.uid;
      
      return (
        <div 
          key={message.id} 
          className={`message ${isOwnMessage ? 'own-message' : 'other-message'}`}
        >
          <div className="message-content">
            <p>{message.content}</p>
            <span className="message-time">
              {formatMessageTime(message.timestamp)}
              {isOwnMessage && message.isRead && <span className="read-indicator"> ✓</span>}
            </span>
          </div>
        </div>
      );
    });
  };

  if (loading) {
    return <div className="loading">Loading conversations...</div>;
  }

  if (!currentUser) {
    return (
      <div className="messages-page">
        <h2>Messages</h2>
        <p>Please sign in to view your messages.</p>
      </div>
    );
  }

  const otherParticipant = selectedConversation 
    ? getOtherParticipant(selectedConversation, currentUser.uid)
    : null;

  return (
    <div className="messages-page">
      <div className="messages-header">
        <h2>Messages</h2>
        <p>Connect and communicate with providers and practitioners</p>
      </div>
      
      <div className="messages-container">
        <div className="conversations-sidebar">
          <div className="conversations-header">
            <h3>Conversations</h3>
          </div>
          <div className="conversations-list">
            {renderConversationList()}
          </div>
        </div>
        
        <div className="message-area">
          {selectedConversation && (
            <div className="message-header">
              <div className="recipient-info">
                <div className="recipient-avatar">
                  {otherParticipant.profilePicture ? (
                    <img 
                      src={otherParticipant.profilePicture} 
                      alt={otherParticipant.firstName} 
                    />
                  ) : (
                    <div className="default-avatar">
                      {otherParticipant.firstName?.[0]}{otherParticipant.lastName?.[0]}
                    </div>
                  )}
                </div>
                <div className="recipient-details">
                  <h3>{otherParticipant.firstName} {otherParticipant.lastName}</h3>
                  <span className="recipient-type">
                    {otherParticipant.userType === 'provider' ? 'Provider' : 'Learner'}
                  </span>
                </div>
              </div>
            </div>
          )}
          
          <div className="messages-list">
            {renderMessages()}
          </div>
          
          {selectedConversation && (
            <form onSubmit={handleSendMessage} className="message-input-area">
              <div className="message-input-container">
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={`Message ${otherParticipant?.firstName || 'recipient'}...`}
                  rows="3"
                  maxLength="1000"
                  disabled={sending}
                />
                <button 
                  type="submit" 
                  className="send-button"
                  disabled={sending || !newMessage.trim()}
                >
                  {sending ? 'Sending...' : 'Send'}
                </button>
              </div>
              <div className="character-count">
                {newMessage.length}/1000
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Messages;